import { authClient } from "@/lib/auth/auth";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getSmtpProvider } from "@/apis/custom/customRoutes";
import {
  GoogleProviderIcon,
  MicrosoftProviderIcon,
} from "@/components/shared/auth/auth-provider-icons";
import { TextInput } from "@/components/shared/form/text-input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { FRONTEND_URL } from "@/lib/constants/constants";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (search.redirect as string | undefined) || "/",
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();

  const [sending, setSending] = useState(false);
  const form = useForm<{ email: string | null }>({
    defaultValues: { email: "" },
  });

  const handleSignIn = async (provider: "google" | "microsoft") => {
    await authClient.signIn.social({
      provider,
      callbackURL: FRONTEND_URL,
      errorCallbackURL: `${FRONTEND_URL}/auth-error`,
    });
  };

  const handleEmailSignIn = async ({ email }: { email: string | null }) => {
    if (!email) {
      form.setError("email", { type: "manual", message: t("email_required") });
      return;
    }
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      form.setError("email", { type: "manual", message: t("email_required") });
      return;
    }

    setSending(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: normalized,
        type: "sign-in",
      });

      if (result.error) {
        form.setError("email", {
          type: "manual",
          message: result.error.message ?? t("auth.email.send_failed"),
        });
        return;
      }

      const searchParams: Record<string, string> = {
        email: normalized,
        redirectTo: redirectTo,
      };

      try {
        const { smtpProvider } = await getSmtpProvider(normalized);
        if (smtpProvider) searchParams.smtpProvider = smtpProvider;
      } catch (error) {
        console.warn("Error retrieving SMTP provider:", error);
      }

      await navigate({
        to: "/otp-code",
        search: searchParams,
      });
    } catch (e) {
      form.setError("email", {
        type: "manual",
        message: e instanceof Error ? e.message : t("auth.email.send_failed"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex items-center gap-2 self-center font-medium">
          <img
            src="/logoTabular.png"
            alt="Skipper Logo"
            width="100"
            height="100"
            style={{
              borderRadius: "10%",
            }}
          />
        </div>
        <div className={cn("flex flex-col gap-6")}>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">
                {t("welcome_to_tabula")}
              </CardTitle>
              <CardDescription>
                {t("welcome_to_tabula_description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                <Form {...form}>
                  <form
                    className="grid gap-3"
                    onSubmit={form.handleSubmit(handleEmailSignIn)}
                  >
                    <TextInput
                      name="email"
                      type="email"
                      placeholder={t("auth.email.placeholder")}
                      disabled={sending}
                      autoComplete="email"
                      inputMode="email"
                      onFocus={() => {
                        if (form.formState.errors.email) {
                          form.clearErrors("email");
                        }
                      }}
                    />

                    <Button className="w-full" type="submit" disabled={sending}>
                      {sending
                        ? t("auth.email.sending")
                        : t("auth.email.continue")}
                    </Button>
                  </form>
                </Form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t("or")}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => handleSignIn("google")}
                  >
                    <GoogleProviderIcon />
                    {t("sign_in_with_google")}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => handleSignIn("microsoft")}
                  >
                    <MicrosoftProviderIcon />
                    {t("sign_in_with_microsoft")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
